import { useEffect, useState, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Singleton para capturar o evento o mais cedo possível,
// antes mesmo de qualquer componente React montar.
let cachedPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<(p: BeforeInstallPromptEvent | null) => void>();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault();
    cachedPrompt = e as BeforeInstallPromptEvent;
    listeners.forEach((l) => l(cachedPrompt));
  });
  window.addEventListener('appinstalled', () => {
    cachedPrompt = null;
    listeners.forEach((l) => l(null));
  });
}

function detectIOS() {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
}

function detectAndroid() {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent);
}

function detectIOSSafari() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIOSDevice = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
  if (!isIOSDevice) return false;
  return /Safari/.test(ua) && /Version\//.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|FBAN|FBAV|Instagram|Line/.test(ua);
}

function detectFirefox() {
  if (typeof navigator === 'undefined') return false;
  return /Firefox|FxiOS/i.test(navigator.userAgent);
}

function detectEdge() {
  if (typeof navigator === 'undefined') return false;
  return /Edg\/|EdgA\/|EdgiOS\//i.test(navigator.userAgent);
}

function detectSamsung() {
  if (typeof navigator === 'undefined') return false;
  return /SamsungBrowser/i.test(navigator.userAgent);
}

function detectOpera() {
  if (typeof navigator === 'undefined') return false;
  return /OPR\/|Opera|OPiOS/i.test(navigator.userAgent);
}

function detectStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true
  );
}

function detectInAppBrowser() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /FBAN|FBAV|FB_IAB|Instagram|Line|MicroMessenger|Twitter|TikTok|Pinterest|LinkedInApp|GSA\//.test(ua);
}

export function usePwaInstall() {
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(cachedPrompt);
  const [isStandalone, setIsStandalone] = useState(detectStandalone());

  useEffect(() => {
    const listener = (p: BeforeInstallPromptEvent | null) => setPrompt(p);
    listeners.add(listener);
    if (cachedPrompt !== prompt) setPrompt(cachedPrompt);

    const onInstalled = () => setIsStandalone(true);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      listeners.delete(listener);
      window.removeEventListener('appinstalled', onInstalled);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isIOS = detectIOS();
  const isAndroid = detectAndroid();
  const isIOSSafari = detectIOSSafari();
  const isInApp = detectInAppBrowser();
  const isFirefox = detectFirefox();
  const isEdge = detectEdge();
  const isSamsung = detectSamsung();
  const isOpera = detectOpera();
  const canInstall = !!prompt && !isStandalone;

  const promptInstall = useCallback(async () => {
    if (!prompt) return 'unavailable' as const;
    try {
      await prompt.prompt();
      const { outcome } = await prompt.userChoice;
      cachedPrompt = null;
      listeners.forEach((l) => l(null));
      return outcome;
    } catch {
      return 'error' as const;
    }
  }, [prompt]);

  return {
    canInstall,
    isIOS,
    isAndroid,
    isIOSSafari,
    isStandalone,
    isInApp,
    isFirefox,
    isEdge,
    isSamsung,
    isOpera,
    promptInstall,
  };
}
